import { useState, useRef, useEffect } from 'react';
import type { Module } from '@/types/modules';
import { EditableModuleTitle } from '../_infrastructure/EditableModuleTitle';

import { useSession } from '@/features/auth/hooks/useSession';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query/keys';
import { getActiveWorkspaceId } from '@/lib/api/workspace-context';
import { fetchConversationSnapshot, syncConversationModules } from '@/features/conversations/lib/conversationAdapter';
import '../_infrastructure/ModuleStyles.css';

interface CalendarModuleProps {
  module: Module;
  conversationId?: string;
  [legacyProp: string]: unknown;
}

export default function CalendarModule(props: CalendarModuleProps) {
  const { module, conversationId: explicitConversationId } = props;
  const conversationId = explicitConversationId ?? '';
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const optionsMenuRef = useRef<HTMLDivElement>(null);
  const gearButtonRef = useRef<HTMLButtonElement>(null);
  const titleRef = useRef<{ startEdit: () => void }>(null);

  // Listen for mobile module options open event
  useEffect(() => {
    const handleOpenOptions = (event: CustomEvent) => {
      const { moduleId } = event.detail || {};
      if (moduleId === module.id) {
        setShowOptionsMenu(true);
      }
    };
    window.addEventListener('xavior:open-module-options', handleOpenOptions as EventListener);
    return () => window.removeEventListener('xavior:open-module-options', handleOpenOptions as EventListener);
  }, [module.id]);

  const { token } = useSession();
  const queryClient = useQueryClient();
  const workspaceId = getActiveWorkspaceId();

  // Handle gear button click and calculate menu position
  const handleGearClick = () => {
    if (!showOptionsMenu && gearButtonRef.current) {
      const rect = gearButtonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.top,
        left: rect.right + 4
      });
    }
    setShowOptionsMenu(!showOptionsMenu);
  };

  // Close options menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!showOptionsMenu) return;

      const target = event.target as HTMLElement;

      // Don't close if clicking the gear button
      if (gearButtonRef.current?.contains(target)) return;

      // Don't close if clicking the options menu dropdown
      if (optionsMenuRef.current?.contains(target)) return;

      // Close the menu for any other click
      setShowOptionsMenu(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showOptionsMenu]);

  const handleEditTitle = () => {
    setShowOptionsMenu(false);
    if (titleRef.current) {
      titleRef.current.startEdit();
    }
  };

  async function handleArchiveModule() {
    setShowOptionsMenu(false);
    if (!workspaceId || !conversationId) return;

    try {
      const snapshot = await fetchConversationSnapshot(token ?? null, workspaceId, conversationId, {
        includeArchivedModules: true,
      });
      const modules = snapshot.conversation.modules ?? [];
      const moduleIndex = modules.findIndex((m: any) => m.id === module.id);
      if (moduleIndex === -1) return;

      // Mark module as archived
      const updatedModules = [...modules];
      updatedModules[moduleIndex] = {
        ...modules[moduleIndex],
        archived: true,
      };

      await syncConversationModules(token ?? null, workspaceId, conversationId, updatedModules);

      queryClient.invalidateQueries({ queryKey: queryKeys.conversations.detail(conversationId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.conversations.all });
    } catch (err) {
      console.error('[CalendarModule] Failed to archive module:', err);
      alert('Failed to archive module');
    }
  }

  const handleNewEvent = () => {
    // TODO: Implement event creation
  };

  return (
    <div className="module">
      <div className="module-accent-rail" style={{ background: 'var(--color-border)' }} />
      <div className="module-header">
        <EditableModuleTitle
          ref={titleRef}
          module={module}
          conversationId={conversationId}
          fallbackLabel="Calendar"
        />
        <div className="module-header-actions">
          <button
            className="module-toolbar-button is-primary"
            onClick={handleNewEvent}
            title="Add event"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            <span className="module-toolbar-label">+ New</span>
          </button>
        </div>
      </div>

      {/* Options Menu Dropdown - rendered outside module with fixed positioning */}
      {showOptionsMenu && menuPosition && (
        <div
          ref={optionsMenuRef}
          style={{
            position: 'fixed',
            top: `${menuPosition.top}px`,
            left: `${menuPosition.left}px`,
            backgroundColor: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: '4px',
            boxShadow: 'var(--popover-shadow, 0 4px 6px rgba(0, 0, 0, 0.1))',
            minWidth: '100px',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 1000,
          }}
        >
          <button
            onClick={handleEditTitle}
            style={{
              width: '100%',
              height: '30px',
              padding: '0 10px',
              background: 'none',
              border: 'none',
              borderBottom: '1px solid var(--color-border)',
              cursor: 'pointer',
              fontSize: '15px',
              color: 'var(--color-text-muted)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: '6px',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--color-surface-hover)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
            Rename
          </button>
          <button
            onClick={handleArchiveModule}
            style={{
              width: '100%',
              height: '30px',
              padding: '0 10px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '15px',
              color: 'var(--color-text-muted)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: '6px',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--color-surface-hover)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="21 8 21 21 3 21 3 8"></polyline>
              <rect x="1" y="3" width="22" height="5"></rect>
              <line x1="10" y1="12" x2="14" y2="12"></line>
            </svg>
            Archive
          </button>
        </div>
      )}

      <div className="module-content">
        <div className="text-center text-gray-500 py-8">Add when you're ready</div>
      </div>
    </div>
  );
}
